import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
	buildSiteEmbedPreviewHtml,
	SITE_EMBED_PREVIEW_ERROR_MESSAGE_TYPE,
} from "./build-site-embed-preview-html";

import type { LangType, SiteEmbedVariant } from "./types";

type SiteEmbedIframePreviewProps = {
	hotelID: string;
	variant: SiteEmbedVariant;
	lang: LangType;
	/** 親に明示的な高さがあるとき、iframe を縦方向に伸ばして余白を埋める */
	fillHeight?: boolean;
};

/**
 * `@chillnn/site-embed` の IIFE を dev-cdn 等から読み込み、iframe 内でプレビューする。
 * - `blob:` URL 実ドキュメントにすることで `srcdoc`＋外部 script 時の `document.currentScript` 不具合を避ける
 * - iframe で `window` が分かれ、ホストの Pay.jp や React DOM と衝突しない
 * - Pay.js は `data-chillnn-preview-skip-payjp` でスタブ（クレジットカード決済の確認は埋め込み先サイトで行う）
 */
export function SiteEmbedIframePreview({
	hotelID,
	variant,
	lang,
	fillHeight = false,
}: SiteEmbedIframePreviewProps) {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [previewErrors, setPreviewErrors] = useState<string[]>([]);

	const previewHtml = useMemo(
		() => buildSiteEmbedPreviewHtml({ hotelID, variant, lang, skipPayjp: true }),
		[hotelID, variant, lang],
	);

	useLayoutEffect(() => {
		const el = iframeRef.current;
		if (!el) return;
		const blob = new Blob([previewHtml], { type: "text/html;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		let revoked = false;
		const revokeOnce = () => {
			if (revoked) return;
			revoked = true;
			URL.revokeObjectURL(url);
		};
		const onLoad = () => {
			el.removeEventListener("load", onLoad);
		};
		el.addEventListener("load", onLoad);
		el.src = url;
		return () => {
			el.removeEventListener("load", onLoad);
			revokeOnce();
		};
	}, [previewHtml]);

	useEffect(() => {
		setPreviewErrors([]);
	}, [hotelID, variant, lang]);

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			const data = event.data as { type?: string; message?: string } | null;
			if (event.source !== iframeRef.current?.contentWindow) {
				return;
			}
			/* blob: 実ドキュメントからの postMessage は serialized origin が "null" になる */
			const parentOrigin = window.location.origin;
			if (event.origin !== parentOrigin && event.origin !== "null") {
				return;
			}
			if (!data || data.type !== SITE_EMBED_PREVIEW_ERROR_MESSAGE_TYPE || typeof data.message !== "string") {
				return;
			}
			const message = data.message;
			setPreviewErrors((prev) => {
				const next = [...prev, message];
				return next.length > 12 ? next.slice(-12) : next;
			});
		};
		window.addEventListener("message", onMessage);
		return () => {
			window.removeEventListener("message", onMessage);
		};
	}, []);

	const iframeClassName = fillHeight
		? "min-h-[480px] w-full flex-1 rounded-md border border-zinc-200 bg-white"
		: "h-[min(520px,70vh)] w-full shrink-0 rounded-md border border-zinc-200 bg-white";

	return (
		<div className={fillHeight ? "flex h-full min-h-0 flex-col gap-2" : "flex min-h-0 flex-col gap-2"}>
			<iframe
				ref={iframeRef}
				key={`${hotelID}-${variant}-${lang}`}
				title="サイト埋め込みプレビュー"
				className={iframeClassName}
				sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
			/>
			{previewErrors.length > 0 ? (
				<div
					role="alert"
					className="max-h-48 overflow-y-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
				>
					<p className="mb-1 font-semibold">プレビュー内のエラー</p>
					<ul className="list-inside list-disc space-y-2 font-mono whitespace-pre-wrap break-words">
						{previewErrors.map((text, i) => (
							<li key={`${i}-${text.slice(0, 40)}`}>{text}</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
}
